import { postPublicEvent, PUBLIC_WEBSITE_SLUG } from "@/lib/publicApi";

type SiteAnalyticsEventType = "pageview" | "heartbeat";

export type SiteAnalyticsEventPayload = {
  event_type: SiteAnalyticsEventType;
  path?: string;
  referrer?: string;
  country?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  user_agent?: string;
  screen_width?: number | null;
  session_id?: string;
};

const SESSION_STORAGE_KEY = "site_analytics_session_id";

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const id = Math.random().toString(16).slice(2, 10) + Math.random().toString(16).slice(2, 10);
    sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage might be blocked; degrade gracefully.
    return "";
  }
}

function getUtmParams(): { utm_source: string; utm_medium: string; utm_campaign: string } {
  const sp = new URLSearchParams(window.location.search);
  return {
    utm_source: sp.get("utm_source") || "",
    utm_medium: sp.get("utm_medium") || "",
    utm_campaign: sp.get("utm_campaign") || "",
  };
}

function getBrowserCountry(): string {
  const locales =
    typeof navigator !== "undefined"
      ? [navigator.language, ...(navigator.languages || [])].filter(Boolean)
      : [];
  for (const locale of locales) {
    const match = String(locale).match(/[-_]([A-Za-z]{2})\b/);
    if (match) return match[1].toUpperCase();
  }
  return "";
}

function buildPayload(eventType: SiteAnalyticsEventType): SiteAnalyticsEventPayload {
  const utm = getUtmParams();
  return {
    event_type: eventType,
    path: window.location.pathname + window.location.search,
    referrer: document.referrer || "",
    country: getBrowserCountry(),
    ...utm,
    screen_width: typeof window !== "undefined" ? window.innerWidth : null,
    session_id: getSessionId(),
  };
}

function postEvent(payload: SiteAnalyticsEventPayload) {
  postPublicEvent(`/${PUBLIC_WEBSITE_SLUG}/site-analytics/event/`, payload);
}

export function trackPageview() {
  postEvent(buildPayload("pageview"));
}

let heartbeatTimer: number | null = null;

export function startHeartbeat(intervalMs = 15000) {
  if (heartbeatTimer) return;
  heartbeatTimer = window.setInterval(() => {
    if (document.visibilityState !== "visible") return;
    postEvent(buildPayload("heartbeat"));
  }, intervalMs);
}

export function stopHeartbeat() {
  if (!heartbeatTimer) return;
  window.clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

